/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import './ServiceBookingConfirmationPage.css';

interface BookingDetails {
  id: string;
  bookingId?: string;
  providerId: string;
  providerName?: string;
  customerId: string;
  date: string;
  time: string;
  address: string;
  description?: string;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  createdAt?: string;
  price?: string;
  category?: string;
}

const ServiceBookingConfirmationPage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [provider, setProvider] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get booking data from location state (from booking page)
  const bookingData = location.state?.booking as BookingDetails | undefined;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/services/booking-confirmation/${providerId}` } });
      return;
    }

    const loadBookingData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If booking data is passed from booking page, use it
        if (bookingData) {
          setBooking(bookingData);
          
          // Fetch provider details
          try {
            const providerData = await apiService.getServiceProvider(providerId!);
            setProvider(providerData);
          } catch (err) {
            console.warn('Could not fetch provider details:', err);
          }
        } else {
          // Try to fetch booking by provider ID and user ID
          // This would require a different endpoint or we use the passed data
          setError('Booking data not found. Please try booking again.');
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error loading booking data:', err);
        setError(err.message || 'Failed to load booking details');
        setLoading(false);
      }
    };

    loadBookingData();
  }, [isAuthenticated, navigate, providerId, bookingData]);

  const handleViewBookings = () => {
    navigate('/services/my-bookings');
  };

  const handleBackToServices = () => {
    navigate('/services');
  };

  const handleContactProvider = () => {
    if (provider?.phone) {
      window.open(`tel:${provider.phone}`, '_self');
    }
  };

  const formatDate = (dateStr: string) => {
    if (dateStr === 'today') {
      return new Date().toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (dateStr === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return dateStr;
  };

  if (loading) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate(-1)}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <div className="service-booking-confirmation-page">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{language === 'hi' ? 'लोड हो रहा है...' : 'Loading booking details...'}</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !booking) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/services')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <div className="service-booking-confirmation-page">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2>{language === 'hi' ? 'त्रुटि' : 'Error'}</h2>
            <p>{error || (language === 'hi' ? 'बुकिंग विवरण नहीं मिला' : 'Booking details not found')}</p>
            <button onClick={() => navigate('/services')} className="back-btn">
              {language === 'hi' ? 'सेवाएं पर वापस जाएं' : 'Back to Services'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate('/services')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <div className="service-booking-confirmation-page">
        <main className="confirmation-main">
          {/* Success Header */}
          <section className="confirmation-header">
            <div className="success-icon">✅</div>
            <h1 className="confirmation-title">
              {language === 'hi' ? 'बुकिंग सफल!' : 'Booking Confirmed!'}
            </h1>
            <p className="confirmation-subtitle">
              {language === 'hi' 
                ? 'आपकी सेवा बुकिंग सफलतापूर्वक पूरी हो गई है' 
                : 'Your service booking has been confirmed successfully'}
            </p>
          </section>

          {/* Booking Details Card */}
          <section className="booking-details-card">
            <h2 className="details-title">
              {language === 'hi' ? 'बुकिंग विवरण' : 'Booking Details'}
            </h2>
            
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">
                  {language === 'hi' ? 'बुकिंग आईडी' : 'Booking ID'}: 
                </span>
                <span className="detail-value">
                  {booking.id || booking.bookingId || 'N/A'}
                </span>
              </div>

              {provider && (
                <div className="detail-item">
                  <span className="detail-label">
                    {language === 'hi' ? 'सेवा प्रदाता' : 'Service Provider'}: 
                  </span>
                  <span className="detail-value">{provider.name || 'N/A'}</span>
                </div>
              )}

              {provider?.category && (
                <div className="detail-item">
                  <span className="detail-label">
                    {language === 'hi' ? 'सेवा श्रेणी' : 'Service Category'}: 
                  </span>
                  <span className="detail-value">{provider.category}</span>
                </div>
              )}

              <div className="detail-item">
                <span className="detail-label">
                  {language === 'hi' ? 'तारीख' : 'Date'}: 
                </span>
                <span className="detail-value">{formatDate(booking.date)}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  {language === 'hi' ? 'समय' : 'Time'}: 
                </span>
                <span className="detail-value">{booking.time}</span>
              </div>

              <div className="detail-item full-width">
                <span className="detail-label">
                  {language === 'hi' ? 'पता' : 'Service Address'}: 
                </span>
                <span className="detail-value">{booking.address}</span>
              </div>

              {booking.description && (
                <div className="detail-item full-width">
                  <span className="detail-label">
                    {language === 'hi' ? 'विवरण' : 'Description'}: 
                  </span>
                  <span className="detail-value">{booking.description}</span>
                </div>
              )}

              {provider?.price && (
                <div className="detail-item">
                  <span className="detail-label">
                    {language === 'hi' ? 'अनुमानित कीमत' : 'Estimated Price'}: 
                  </span>
                  <span className="detail-value price-value">{provider.price}</span>
                </div>
              )}

              <div className="detail-item">
                <span className="detail-label">
                  {language === 'hi' ? 'स्थिति' : 'Status'}: 
                </span>
                <span className={`status-badge status-${booking.status}`}>
                  {booking.status === 'pending' 
                    ? (language === 'hi' ? 'लंबित' : 'Pending')
                    : booking.status === 'confirmed'
                    ? (language === 'hi' ? 'पुष्टि की गई' : 'Confirmed')
                    : booking.status === 'in_progress'
                    ? (language === 'hi' ? 'प्रगति में' : 'In Progress')
                    : booking.status === 'completed'
                    ? (language === 'hi' ? 'पूर्ण' : 'Completed')
                    : (language === 'hi' ? 'रद्द' : 'Cancelled')
                  }
                </span>
              </div>
            </div>
          </section>

          {/* Next Steps */}
          <section className="next-steps-section">
            <h3 className="next-steps-title">
              {language === 'hi' ? 'अगले कदम' : 'What\'s Next?'}
            </h3>
            <ul className="next-steps-list">
              <li>
                {language === 'hi' 
                  ? 'सेवा प्रदाता आपसे संपर्क करेगा' 
                  : 'The service provider will contact you'}
              </li>
              <li>
                {language === 'hi' 
                  ? 'आपको बुकिंग की पुष्टि का संदेश मिलेगा' 
                  : 'You will receive a confirmation message'}
              </li>
              <li>
                {language === 'hi' 
                  ? 'आप "मेरी बुकिंग" में अपनी बुकिंग देख सकते हैं' 
                  : 'You can view your booking in "My Bookings"'}
              </li>
            </ul>
          </section>

          {/* Action Buttons */}
          <section className="confirmation-actions">
            {provider?.phone && (
              <button
                className="action-btn contact-btn"
                onClick={handleContactProvider}
              >
                📞 {language === 'hi' ? 'प्रदाता से संपर्क करें' : 'Contact Provider'}
              </button>
            )}
            <button
              className="action-btn bookings-btn"
              onClick={handleViewBookings}
            >
              📋 {language === 'hi' ? 'मेरी बुकिंग देखें' : 'View My Bookings'}
            </button>
            <button
              className="action-btn back-btn"
              onClick={handleBackToServices}
            >
              {language === 'hi' ? 'सेवाएं पर वापस जाएं' : 'Back to Services'}
            </button>
          </section>
        </main>
      </div>
    </>
  );
};

export default ServiceBookingConfirmationPage;
